import React from 'react';

import { Box, Grid, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import { PermissiblePurposeStatus } from 'src/api';
import { Button } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { usePermissiblePurposeUpdate } from 'src/hooks/permissiblePurposes';

import {
  PermissiblePurposeDeclineModalFields,
  PermissiblePurposeDeclineModalValues,
} from './types';
import { permissiblePurposeDeclineSchema } from './validation';

interface PermissiblePurposeDeclineModalFormProps {
  permissiblePurposeId: string;
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const PermissiblePurposeDeclineModalForm: React.FC<
  PermissiblePurposeDeclineModalFormProps
> = ({ permissiblePurposeId, closeModal, switchClosability }) => {
  const { updatePermissiblePurpose } = usePermissiblePurposeUpdate();

  const onSubmit = React.useCallback(
    async (formValues: PermissiblePurposeDeclineModalValues) => {
      switchClosability(false);
      try {
        await updatePermissiblePurpose({
          variables: {
            id: permissiblePurposeId,
            data: {
              status: PermissiblePurposeStatus.Declined,
              denialReason: formValues.denialReason,
            },
          },
        });
        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [switchClosability, updatePermissiblePurpose, permissiblePurposeId, closeModal],
  );

  return (
    <Box>
      <Typography variant="body1" color="text.secondary">
        Please provide the denial reason to explain the problem with the selected Permissible
        Purpose
      </Typography>
      <Form<PermissiblePurposeDeclineModalValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: '16px' }}
        initialValues={{ denialReason: '' }}
        validate={validateWithYupSchema(permissiblePurposeDeclineSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <Grid size={12}>
                <Field
                  name={PermissiblePurposeDeclineModalFields.denialReason}
                  label="Denial Reason"
                  required
                  fullWidth
                  disabled={submitting}
                  component={TextField}
                />
              </Grid>
              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="infoButton"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Submit
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};
